import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { OneUser, UserTokenCheck } from '../_models/users';
import { UserCompanyDetails } from '../_models/UserCompanyDetails';
import { UsersDirectorDetails } from '../_models/UsersDirectorDetails';
import { UserContactPerson } from '../_models/UserContactPerson';
import { UserOwner } from '../_models/UserOwner';
import { UserInternationalB } from '../_models/UserInternationalB';
import { UserLocalBranch } from '../_models/UserLocalBranch';
import { UserTransactionDetails } from '../_models/UserTransactionDetails';
import { UserComplianceDetails } from '../_models/UserCompliance';
import { UserBankAccount } from '../_models/UserBankAccount';
import { UserRelatedEntity } from '../_models/UserRelatedEntity';
import { UserDocumentType } from '../_models/documentType';

@Injectable({
    providedIn: 'root'
  })
  export class ApiService {
    baseUrl = environment.baseUrl;
    product_key: string|number = Math.floor(Math.random() * Math.floor(999999));
  
    constructor(
      private http: HttpClient,
    ) {}

    /**
     * API to check public key
     * @param token
     */
    checkToken(token): Observable<UserTokenCheck> {
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
            })
        }

        const responseData = this.get(this.baseUrl + 'kyc/checkToken/' + token, httpOptions);

        return responseData;
    }

    setPin(data, token): Observable<OneUser> {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }
        const responseData = this.put(this.baseUrl + 'kyc/setPin', JSON.stringify(data), httpOptions);
    
        return responseData;
    }

    verifyPin(data, token): Observable<OneUser> {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }
        const responseData = this.post(this.baseUrl + 'kyc/verifyPin', JSON.stringify(data), httpOptions);
    
        return responseData;
    }

    getUserFromToken(token) {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }
        const responseData = this.get(this.baseUrl + 'kyc/profile', httpOptions);

        return responseData;
    }

    getDocumentType(token): Promise<UserDocumentType> {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }

        const responseData = this.http.get<UserDocumentType>(this.baseUrl + 'kyc/documentType/get', httpOptions);

        // Convert the Observable to a Promise
        return firstValueFrom(
            this.http.get<UserDocumentType>(this.baseUrl + 'kyc/documentType/get', httpOptions)
        );
    }

    transactionDetailsCreate(data, token): Observable<UserTransactionDetails> {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }
        const responseData = this.post(this.baseUrl + 'kyc/createTransaction', JSON.stringify(data), httpOptions);
    
        return responseData;
    }

    complianceDetailsCreate(data, token): Observable<UserComplianceDetails> {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }
        const responseData = this.post(this.baseUrl + 'kyc/createCompliance', JSON.stringify(data), httpOptions);
    
        return responseData;
    }

    companyDetailsCreate(data, token): Observable<UserCompanyDetails> {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }
        const responseData = this.post(this.baseUrl + 'kyc/createCompany', JSON.stringify(data), httpOptions);
    
        return responseData;
    }

    directorDetailsCreate(data, token): Observable<UsersDirectorDetails> {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }
        const responseData = this.post(this.baseUrl + 'kyc/createDirector', JSON.stringify(data), httpOptions);
    
        return responseData;
    }

    directorDetailsCRemove(ref, token) {
        var gateway_passcode = btoa(this.product_key + "_" + token);

        // request headers
        const httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer '+gateway_passcode,
            'key': this.product_key.toString()
        })
        }
        const responseData = this.delete(this.baseUrl + 'kyc/removeDirector/'+ref, httpOptions);

        return responseData;
    }

    ownerDetailsCreate(data, token): Observable<UserOwner> {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }
        const responseData = this.post(this.baseUrl + 'kyc/createOnwer', JSON.stringify(data), httpOptions);
    
        return responseData;
    }

    relativeDetailsCreate(data, token): Observable<UserRelatedEntity> {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }
        const responseData = this.post(this.baseUrl + 'kyc/createRelative', JSON.stringify(data), httpOptions);
    
        return responseData;
    }

    bankAccountDetailsCreate(data, token): Observable<UserBankAccount> {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }
        const responseData = this.post(this.baseUrl + 'kyc/createBankAccount', JSON.stringify(data), httpOptions);
    
        return responseData;
    }

    intDetailsCreate(data, token): Observable<UserInternationalB> {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }
        const responseData = this.post(this.baseUrl + 'kyc/createInt', JSON.stringify(data), httpOptions);
    
        return responseData;
    }

    localDetailsCreate(data, token): Observable<UserLocalBranch> {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }
        const responseData = this.post(this.baseUrl + 'kyc/createLocal', JSON.stringify(data), httpOptions);
    
        return responseData;
    }

    ownerDetailsCRemove(ref, token) {
        var gateway_passcode = btoa(this.product_key + "_" + token);

        // request headers
        const httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer '+gateway_passcode,
            'key': this.product_key.toString()
        })
        }
        const responseData = this.delete(this.baseUrl + 'kyc/removeOnwer/'+ref, httpOptions);

        return responseData;
    }

    relativeDetailsCRemove(ref, token) {
        var gateway_passcode = btoa(this.product_key + "_" + token);

        // request headers
        const httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer '+gateway_passcode,
            'key': this.product_key.toString()
        })
        }
        const responseData = this.delete(this.baseUrl + 'kyc/removeRelative/'+ref, httpOptions);

        return responseData;
    }

    bankAccountRemove(ref, token) {
        var gateway_passcode = btoa(this.product_key + "_" + token);

        // request headers
        const httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer '+gateway_passcode,
            'key': this.product_key.toString()
        })
        }
        const responseData = this.delete(this.baseUrl + 'kyc/removeBankAccount/'+ref, httpOptions);

        return responseData;
    }

    fileRemove(ref, token) {
        var gateway_passcode = btoa(this.product_key + "_" + token);

        // request headers
        const httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer '+gateway_passcode,
            'key': this.product_key.toString()
        })
        }
        const responseData = this.delete(this.baseUrl + 'kyc/removeFile/'+ref, httpOptions);

        return responseData;
    }

    intDetailsCRemove(ref, token) {
        var gateway_passcode = btoa(this.product_key + "_" + token);

        // request headers
        const httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer '+gateway_passcode,
            'key': this.product_key.toString()
        })
        }
        const responseData = this.delete(this.baseUrl + 'kyc/removeInt/'+ref, httpOptions);

        return responseData;
    }


    localDetailsCRemove(ref, token) {
        var gateway_passcode = btoa(this.product_key + "_" + token);

        // request headers
        const httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer '+gateway_passcode,
            'key': this.product_key.toString()
        })
        }
        const responseData = this.delete(this.baseUrl + 'kyc/removeLocal/'+ref, httpOptions);

        return responseData;
    }

    contactDetailsCreate(data, token): Observable<UserContactPerson> {
        var gateway_passcode = btoa(this.product_key + "_" + token);
        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }
        const responseData = this.post(this.baseUrl + 'kyc/createContact', JSON.stringify(data), httpOptions);
    
        return responseData;
    }

    contactDetailsCRemove(ref, token) {
        var gateway_passcode = btoa(this.product_key + "_" + token);

        // request headers
        const httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer '+gateway_passcode,
            'key': this.product_key.toString()
        })
        }
        const responseData = this.delete(this.baseUrl + 'kyc/removeContact/'+ref, httpOptions);

        return responseData;
    }

    uploadFile(id:number, file: File, token) {
        var gateway_passcode = btoa(this.product_key + "_" + token);

        const formData: FormData = new FormData();
        formData.append('file', file, file.name);

        // request headers
        const httpOptions = {
            headers: new HttpHeaders({
                'Authorization': 'Bearer '+gateway_passcode,
                'key': this.product_key.toString()
            })
        }

        const responseData = this.post(this.baseUrl + 'kyc/uploadDocument/' + id, formData, httpOptions);

        return responseData;
    }
    
    submitKyc(token) {
        var gateway_passcode = btoa(this.product_key + "_" + token);

        // request headers
        const httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer '+gateway_passcode,
            'key': this.product_key.toString()
        })
        }
        const responseData = this.post(this.baseUrl + 'kyc/submit', '', httpOptions);

        return responseData;
    }
  
    /**
     * POST Request
     * @param url API URL
     * @param jsonData JSON encoded Data
     * @param httpOptions HTTP Options
     */
    post(url, jsonData, httpOptions): Observable<any> {
      return this.http.post<any>(url, jsonData, httpOptions)
      .pipe(
        retry(1),
        catchError(this.errorHandl))
    }
  
    /**
     * PUT Request
     * @param url API URL
     * @param jsonData JSON encoded Data
     * @param httpOptions HTTP Options
     */
    put(url, jsonData, httpOptions): Observable<any> {
      return this.http.put<any>(url, jsonData, httpOptions)
      .pipe(
        retry(1),
        catchError(this.errorHandl))
    }
  
    
    /**
     * GET request
     * @param url API URL
     * @param httpOptions HTTP Options
     */
    get(url, httpOptions): Observable<any> {
      return this.http.get<any>(url, httpOptions)
      .pipe(
        retry(1),
        catchError(this.errorHandl))
    }
  
    
    /**
     * DELETE request
     * @param url API URL
     * @param httpOptions HTTP Options
     */
    delete(url, httpOptions): Observable<any> {
      return this.http.delete<any>(url, httpOptions)
      .pipe(
        retry(1),
        catchError(this.errorHandl))
    }
  
    errorHandl(error) {
      return throwError("Can not connect to service "+error);
    }
  }